import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api, { asList, sliceList } from '../utils/api';
import API from '../utils/apiconfig';
import { useAuth } from '../context/Authcontext';
import { useGlobalError } from '../context/GlobalErrorContext';
import { getRankDisplay, rankTheme } from '../utils/rankBadge';
import { paymentStatusMeta, formatEmiWindowDisplay } from '../utils/planStatus';
import { formatAppDate } from '../utils/dateFormat';
import PayoutWalletNote from '../components/PayoutWalletNote';

const RANK_COLORS = {
  bronze:   'bg-orange-50/90 text-orange-700 border-orange-200/80',
  silver:   'bg-slate-50 text-slate-600 border-slate-200',
  gold:     'bg-amber-50 text-amber-700 border-amber-200/80',
  platinum: 'bg-cyan-50 text-cyan-700 border-cyan-200/80',
  diamond:  'bg-violet-50 text-violet-700 border-violet-200/80',
};

/** Medal emoji per tier (profile badge + rank card), same as earlier dashboard */
const RANK_MEDALS = {
  bronze: '🥉',
  silver: '🥈',
  gold: '🥇',
  platinum: '💎',
  diamond: '✨',
};

function Svg({ className = 'h-4 w-4', children }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className} aria-hidden>
      {children}
    </svg>
  );
}
const IconSparkles = (p) => <Svg {...p}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.847a4.5 4.5 0 003.09 3.09L15.75 12l-2.847.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423L16.5 15.75l.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" /></Svg>;
const IconChart = (p) => <Svg {...p}><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></Svg>;
const IconCalendar = (p) => <Svg {...p}><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5a2.25 2.25 0 002.25-2.25m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5a2.25 2.25 0 012.25 2.25v7.5" /></Svg>;
const IconGlobe = (p) => <Svg {...p}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /></Svg>;
const IconWallet = (p) => <Svg {...p}><path strokeLinecap="round" strokeLinejoin="round" d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0v-1.5a2.25 2.25 0 00-2.25-2.25H15a3 3 0 10-6 0H5.25A2.25 2.25 0 003 10.5V12" /></Svg>;
const IconBank = (p) => <Svg {...p}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" /></Svg>;
const IconUsers = (p) => <Svg {...p}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.733-3.042 4.125 4.125 0 00-2.708 1.622 9.37 9.37 0 002.695 0zM12 12.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5zm-9.375 6.375a2.625 2.625 0 015.25 0v1.125c0 .621-.504 1.125-1.125 1.125h-3c-.621 0-1.125-.504-1.125-1.125v-1.125zm14.625 0a2.625 2.625 0 015.25 0v1.125c0 .621-.504 1.125-1.125 1.125h-3c-.621 0-1.125-.504-1.125-1.125v-1.125z" /></Svg>;
const IconLink = (p) => <Svg {...p}><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" /></Svg>;

const ACTIVE_MEMBERSHIP_STATUSES = new Set(['active', 'approved', 'paid']);

function getMembershipAlert(status, reason, { canSwitchPlan = false } = {}) {
  switch (status) {
    case 'pending':
      return {
        title: 'Activate your membership',
        description:
          'Your payment screenshot was submitted. Membership activates after admin approval.',
        action: { kind: 'disabled', label: 'Awaiting admin review' },
        shellClass:
          'border-amber-200/80 bg-gradient-to-r from-amber-50/95 via-orange-50/85 to-amber-100/70 shadow-[0_16px_40px_-20px_rgba(245,158,11,0.35)]',
        titleClass: 'text-amber-950',
        bodyClass: 'text-amber-900/75',
        buttonClass:
          'border-amber-300/90 bg-white text-amber-950 cursor-not-allowed opacity-95',
      };
    case 'rejected':
      return {
        title: 'Payment rejected — re-upload required',
        description:
          reason ||
          (canSwitchPlan
            ? 'Admin rejected your first payment proof. You may choose the correct plan once (lifetime), then upload a new screenshot.'
            : 'Admin rejected your payment proof. Pay again and upload a new screenshot to activate your membership.'),
        action: canSwitchPlan
          ? { kind: 'link', to: '/plans', label: 'Choose correct plan' }
          : { kind: 'continue', label: 'Re-upload payment' },
        shellClass:
          'border-rose-200/80 bg-gradient-to-r from-rose-50/90 via-amber-50/80 to-orange-50/70 shadow-[0_16px_40px_-20px_rgba(244,63,94,0.2)]',
        titleClass: 'text-rose-950',
        bodyClass: 'text-rose-900/80',
        buttonClass:
          'bg-gradient-to-r from-violet-500 to-indigo-600 text-white shadow-lg shadow-indigo-300/50 hover:from-violet-600 hover:to-indigo-700',
      };
    default:
      return {
        title: 'Activate your membership',
        description:
          'Choose a plan and upload payment proof to unlock earnings and referrals.',
        action: { kind: 'link', to: '/plans', label: 'Activate' },
        shellClass:
          'border-amber-200/80 bg-gradient-to-r from-amber-50/95 via-orange-50/85 to-amber-100/70 shadow-[0_16px_40px_-20px_rgba(245,158,11,0.35)]',
        titleClass: 'text-amber-950',
        bodyClass: 'text-amber-900/75',
        buttonClass:
          'bg-gradient-to-r from-violet-500 to-indigo-600 text-white shadow-lg shadow-indigo-300/50 hover:from-violet-600 hover:to-indigo-700',
      };
  }
}

function StatCard({ label, value, sub, warning, valueClassName, icon, iconWellClass }) {
  return (
    <div className="group relative overflow-hidden rounded-xl border border-white/80 bg-white/70 p-3 shadow-[0_8px_30px_-12px_rgba(15,23,42,0.2)] backdrop-blur-sm transition-all duration-200 hover:border-indigo-100/90 hover:shadow-[0_12px_36px_-12px_rgba(99,102,241,0.25)]">
      <div className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-gradient-to-br from-indigo-100/50 via-transparent to-cyan-100/40 opacity-60" />
      <div className="relative flex items-start justify-between gap-1.5">
        <p className="text-[9px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
        {icon && (
          <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${iconWellClass || 'bg-gradient-to-br from-slate-100 to-slate-50 text-slate-600'}`}>
            {icon}
          </div>
        )}
      </div>
      <p className={`relative mt-1.5 text-lg font-semibold leading-tight tracking-tight ${valueClassName || 'text-slate-700'}`} style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>{value}</p>
      {sub && <p className="relative mt-0.5 text-[10px] font-normal text-slate-500">{sub}</p>}
      {warning && (
        <p className="relative mt-1 text-[10px] font-medium leading-snug text-amber-700">{warning}</p>
      )}
    </div>
  );
}

function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { reportError } = useGlobalError();
  const [summary,     setSummary]     = useState(null);
  const [txns,        setTxns]        = useState([]);
  const [membership,  setMembership]  = useState(null);
  const [linkCopied,  setLinkCopied]  = useState(false);
  const [snackMessage, setSnackMessage] = useState('');

  useEffect(() => {
    const message = location.state?.snackMessage;
    if (!message) return undefined;
    setSnackMessage(message);
    const timer = window.setTimeout(() => setSnackMessage(''), 4000);
    return () => window.clearTimeout(timer);
  }, [location.state?.snackMessage]);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const isAborted = (err) =>
      err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError';

    Promise.all([
      api.get(API.EARNINGS_SUMMARY, { signal }),
      api.get(API.TRANSACTIONS, { signal }),
      api.get(API.MEMBERSHIP, { signal }),
      api.get(API.PROFILE, { signal }),
    ])
      .then(([sumRes, txnRes, memRes, profRes]) => {
        try {
          setSummary(sumRes.data);
          setTxns(sliceList(txnRes?.data, 6));
          setMembership(memRes.data);
          updateUser(profRes.data);
        } catch (loadErr) {
          reportError(loadErr);
        }
      })
      .catch((err) => {
        if (isAborted(err)) return;
        reportError(err);
      });

    return () => controller.abort();
  }, [user?.id, updateUser, reportError]);

  const rankBadge = getRankDisplay(user);
  const { colorClass: rankColor, icon: rankMedal } = rankTheme(
    rankBadge.themeKey,
    RANK_COLORS,
    RANK_MEDALS
  );
  const membershipStatus = String(
    user?.membership_status || membership?.status || 'none'
  ).toLowerCase();
  const isMembershipActive =
    ACTIVE_MEMBERSHIP_STATUSES.has(membershipStatus) || membership?.is_active === true;
  const showMembershipAlert = !ACTIVE_MEMBERSHIP_STATUSES.has(membershipStatus);
  const rejectionReason = String(user?.reason || '').trim();
  const payMeta = paymentStatusMeta(user);
  const alertStatus = payMeta.awaitingReview
    ? 'pending'
    : membershipStatus === 'rejected' || payMeta.tone === 'rejected'
      ? 'rejected'
      : 'default';
  const membershipAlert = getMembershipAlert(alertStatus, rejectionReason, {
    canSwitchPlan: Boolean(user?.can_one_time_plan_switch),
  });
  const daysRemaining = user?.days_remaining ?? membership?.days_remaining ?? 0;
  const emiWindow = formatEmiWindowDisplay(daysRemaining, {
    isActive: isMembershipActive,
    overdueDays: user?.emi_days_overdue ?? 0,
    blockCountdown: user?.emi_block_countdown_days,
    windowStatus: user?.emi_payment_window_status,
  });
  const monthlyEarnings = asList(summary?.monthly_earnings);
  const maxMonth = monthlyEarnings.reduce((m, e) => Math.max(m, e.points), 1) || 1;

  const txnTypeLabel = {
    referral_join:    { label: 'New referral joined',  color: 'text-emerald-600', bg: 'bg-emerald-50', sign: '+' },
    referral_renewal: { label: 'Referral renewed',     color: 'text-blue-600',    bg: 'bg-blue-50',    sign: '+' },
    withdrawal:       { label: 'Withdrawal',           color: 'text-orange-600',  bg: 'bg-orange-50',  sign: '-' },
    subscription_fee: { label: 'Membership fee',       color: 'text-red-500',     bg: 'bg-red-50',     sign: '-' },
    bonus:            { label: 'Admin bonus',          color: 'text-violet-600',  bg: 'bg-violet-50',  sign: '+' },
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(user?.referral_code || '').then(() => {
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2200);
    }).catch(() => {
      window.alert('Could not copy to clipboard. Please copy the code manually.');
    });
  };

  const handleContinuePayment = async () => {
    try {
      const planRes = await api.get(API.CURRENT_PLAN);
      const planKey = planRes.data?.current_plan;
      if (!planKey || planKey === 'none') {
        navigate('/plans');
        return;
      }
      const planType = planKey === 'full_settlement' ? 'full_settlement' : 'monthly_emi';
      const selectedPlan = planKey === 'full_settlement' ? 'full' : 'emi';
      const byType = await api.get(API.PLAN_BY_TYPE(planType));
      const data = byType.data;
      if (!data?.success || data.can_enroll === false) {
        setSnackMessage(
          data?.enrollment_blocked_reason
            || data?.message
            || 'Unable to continue payment.'
        );
        return;
      }
      const planDetails = data.plan || {};
      navigate('/payment', {
        state: {
          selectedPlan,
          planName: planDetails.plan_name,
          amountLabel: planDetails.amount_label,
          amount: planDetails.amount || planDetails.amt,
          currentPlan: planDetails.current_plan,
          catalogPlanId: data.catalog_plan_id,
        },
      });
    } catch (err) {
      setSnackMessage(
        err.response?.data?.message
          || err.response?.data?.error
          || 'Something went wrong. Please try again.'
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-indigo-50 to-cyan-100 py-5 sm:py-8">
      {snackMessage ? (
        <div className="fixed top-5 right-5 z-[100] max-w-sm bg-slate-900 text-white text-sm font-semibold px-5 py-3 rounded-2xl shadow-2xl">
          {snackMessage}
        </div>
      ) : null}
      <div className="mx-auto w-full max-w-5xl space-y-4 px-4 sm:space-y-5 sm:px-6 lg:px-8">

        {/* Profile header */}
        <div className="relative overflow-hidden rounded-2xl border border-white/90 bg-white/85 p-4 shadow-[0_20px_50px_-24px_rgba(15,23,42,0.35)] backdrop-blur-md sm:p-5">
          <div className="pointer-events-none absolute -right-20 -top-20 h-44 w-44 rounded-full bg-gradient-to-br from-indigo-200/50 via-violet-100/30 to-cyan-200/40 blur-2xl" />
          <div className="relative flex flex-row items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-indigo-400/35 to-cyan-400/30 blur-sm scale-110" aria-hidden />
                <div
                  className="relative flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-gradient-to-br from-indigo-600 via-violet-600 to-slate-800 text-lg font-semibold text-white ring-2 ring-white/90 shadow-lg sm:text-xl"
                  style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
                >
                  {user?.username?.[0]?.toUpperCase()}
                </div>
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <h1 className="text-lg font-semibold tracking-tight text-slate-800 sm:text-xl" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                    {user?.username}
                  </h1>
                  {rankBadge.show && (
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${rankColor}`}>
                      <span className="text-[13px] leading-none" aria-hidden>{rankMedal}</span>
                      {rankBadge.label}
                    </span>
                  )}
                </div>
                <p className="mt-0.5 truncate text-xs text-slate-500">{user?.email}</p>
              </div>
            </div>
            <div
              className={`flex shrink-0 flex-col items-end rounded-xl border px-2.5 py-1.5 text-right shadow-sm sm:px-3 sm:py-2 ${
                isMembershipActive
                  ? 'border-emerald-200/80 bg-gradient-to-br from-emerald-50/95 to-white'
                  : 'border-rose-200/70 bg-gradient-to-br from-rose-50/90 to-white'
              }`}
            >
              <span className={`text-[8px] font-semibold uppercase tracking-wider ${isMembershipActive ? 'text-emerald-600' : 'text-rose-500'}`}>Membership</span>
              <span className={`mt-0.5 flex items-center justify-end gap-1 text-[11px] font-semibold ${isMembershipActive ? 'text-emerald-800' : 'text-rose-800'}`}>
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${isMembershipActive ? 'bg-emerald-500' : 'bg-rose-500'}`} aria-hidden />
                {isMembershipActive ? 'Active' : 'Inactive'}
              </span>
              <span className={`mt-0.5 text-[10px] font-normal leading-tight ${isMembershipActive ? 'text-emerald-600/90' : 'text-rose-600/90'}`}>
                {isMembershipActive
                  ? emiWindow.badge
                  : 'No membership'}
              </span>
            </div>
          </div>
        </div>

        {showMembershipAlert && (
          <div
            className={`relative overflow-hidden rounded-2xl border p-4 sm:flex sm:items-center sm:justify-between sm:gap-4 sm:p-4 ${membershipAlert.shellClass}`}
          >
            <div className="absolute right-0 top-0 h-28 w-28 translate-x-1/4 -translate-y-1/4 rounded-full bg-orange-200/30 blur-2xl" aria-hidden />
            <div className="relative min-w-0 max-w-lg flex-1">
              <p
                className={`text-sm font-semibold sm:text-base ${membershipAlert.titleClass}`}
                style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
              >
                {membershipAlert.title}
              </p>
              <p className={`mt-1 text-xs leading-relaxed ${membershipAlert.bodyClass}`}>
                {membershipAlert.description}
              </p>
            </div>
            {membershipAlert.action.kind === 'disabled' ? (
              <button
                type="button"
                disabled
                aria-disabled="true"
                className={`relative mt-3 inline-flex items-center justify-center gap-1.5 rounded-xl border px-4 py-2 text-xs font-semibold sm:mt-0 sm:flex-shrink-0 ${membershipAlert.buttonClass}`}
              >
                {membershipAlert.action.label}
                <span aria-hidden className="text-[11px] opacity-90">→</span>
              </button>
            ) : membershipAlert.action.kind === 'continue' ? (
              <button
                type="button"
                onClick={handleContinuePayment}
                className={`relative mt-3 inline-flex items-center justify-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold transition active:scale-[0.99] sm:mt-0 sm:flex-shrink-0 ${membershipAlert.buttonClass}`}
              >
                {membershipAlert.action.label}
                <span aria-hidden className="text-[11px] opacity-90">→</span>
              </button>
            ) : (
              <Link
                to={membershipAlert.action.to}
                className={`relative mt-3 inline-flex items-center justify-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold no-underline transition active:scale-[0.99] sm:mt-0 sm:flex-shrink-0 ${membershipAlert.buttonClass}`}
              >
                {membershipAlert.action.label}
                <span aria-hidden className="text-[11px] opacity-90">→</span>
              </Link>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-2.5">
          <StatCard
            label="Available Points"
            value={(user?.reward_points ?? 0).toLocaleString('en-IN')}
            sub="₹1 per point"
            valueClassName="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent"
            icon={<IconSparkles className="h-3.5 w-3.5 text-amber-600" />}
            iconWellClass="bg-gradient-to-br from-amber-100 to-orange-50 text-amber-700"
          />
          <StatCard
            label="Total Earned"
            value={(summary?.total_earned_points ?? 0).toLocaleString('en-IN')}
            sub="All time"
            valueClassName="bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent"
            icon={<IconChart className="h-3.5 w-3.5 text-indigo-600" />}
            iconWellClass="bg-gradient-to-br from-indigo-100 to-violet-50 text-indigo-700"
          />
          <StatCard
            label="Rank"
            value={rankBadge.show ? rankBadge.label : '—'}
            sub={rankBadge.show ? 'Current tier' : 'Not ranked'}
            valueClassName={rankBadge.show ? 'text-slate-800' : 'text-slate-400'}
            icon={
              rankBadge.show ? (
                <span className="text-sm leading-none">{rankMedal}</span>
              ) : (
                <span className="text-xs font-medium text-slate-400">—</span>
              )
            }
            iconWellClass="bg-gradient-to-br from-violet-100 to-fuchsia-50 text-violet-700"
          />
          <StatCard
            label="Next EMI window"
            value={emiWindow.value}
            sub={emiWindow.hint || (isMembershipActive ? 'Payment window' : 'No active plan')}
            warning={emiWindow.warning}
            valueClassName={emiWindow.value === 'Overdue' ? 'text-rose-700' : 'text-slate-700'}
            icon={<IconCalendar className="h-3.5 w-3.5 text-cyan-700" />}
            iconWellClass="bg-gradient-to-br from-cyan-100 to-sky-50 text-cyan-800"
          />
        </div>

        {summary?.open_payout ? (
          <PayoutWalletNote snapshot={summary.open_payout} />
        ) : null}

        {/* Monthly earnings chart */}
        {monthlyEarnings.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-white/90 bg-white/80 p-4 shadow-[0_16px_44px_-20px_rgba(15,23,42,0.3)] backdrop-blur-md">
            <div className="mb-4 flex items-end justify-between gap-3">
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-widest text-indigo-500/90">Performance</p>
                <p className="mt-0.5 text-sm font-semibold text-slate-800" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>Monthly earnings</p>
                <p className="text-[10px] text-slate-500">Last 6 months</p>
              </div>
            </div>
            <div className="flex h-20 items-end gap-1.5 sm:gap-2">
              {monthlyEarnings.map((m, i) => {
                const pct = Math.max(10, (m.points / maxMonth) * 100);
                return (
                  <div key={i} className="flex flex-1 flex-col items-center gap-1">
                    <span className="text-[9px] font-medium text-slate-500">{m.points}</span>
                    <div className="flex h-14 w-full items-end justify-center">
                      <div
                        className="w-full max-w-[2rem] rounded-t-lg bg-gradient-to-t from-indigo-600 via-violet-500 to-cyan-400 shadow-sm transition-all duration-700"
                        style={{ height: `${pct}%` }}
                      />
                    </div>
                    <span className="text-[9px] font-medium uppercase text-slate-400">{m.month.split(' ')[0]}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Referral code */}
        <div className="overflow-hidden rounded-2xl border border-white/90 bg-white/80 shadow-[0_16px_44px_-20px_rgba(15,23,42,0.28)] backdrop-blur-md">
          <div className="border-b border-indigo-50/80 bg-gradient-to-r from-indigo-50/60 via-white to-cyan-50/50 px-4 py-2.5">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-300/40">
                <IconLink className="h-3.5 w-3.5" />
              </div>
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Your referral code</p>
                <p className="mt-0.5 text-xs text-slate-600">Share to earn when people join</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <span className="inline-flex w-fit max-w-full items-center rounded-xl border border-slate-200/80 bg-slate-50/90 px-3 py-2 font-mono text-sm font-medium tracking-widest text-slate-700">
              {user?.referral_code}
            </span>
            <button
              type="button"
              onClick={copyReferralLink}
              className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-xs font-semibold transition-all sm:flex-shrink-0 ${
                linkCopied
                  ? 'border border-emerald-300/80 bg-emerald-50 text-emerald-800'
                  : 'border border-transparent bg-gradient-to-r from-violet-500 to-indigo-600 text-white shadow-md shadow-indigo-300/40 hover:from-violet-600 hover:to-indigo-700'
              }`}
            >
              {linkCopied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-2.5">
          {[
            { to: '/network',     label: 'My Network',   Icon: IconGlobe,  well: 'from-sky-500 to-blue-600 shadow-sky-300/40' },
            { to: '/earnings',    label: 'Earnings',     Icon: IconWallet, well: 'from-amber-500 to-orange-600 shadow-amber-300/40' },
            { to: '/withdrawal',  label: 'Withdrawals',  Icon: IconBank,   well: 'from-emerald-500 to-teal-600 shadow-emerald-300/40' },
            { to: '/community',   label: 'Community',    Icon: IconUsers,  well: 'from-orange-500 to-rose-500 shadow-rose-300/35' },
          ].map((a, i) => (
            <Link
              key={i}
              to={a.to}
              className="group flex flex-col items-center gap-2 rounded-2xl border border-white/90 bg-white/75 p-3 no-underline shadow-[0_10px_30px_-14px_rgba(15,23,42,0.25)] backdrop-blur-sm transition-all hover:-translate-y-px hover:border-indigo-100 hover:shadow-[0_14px_36px_-12px_rgba(99,102,241,0.28)]"
            >
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${a.well} text-white shadow-lg transition-transform group-hover:scale-105`}>
                <a.Icon className="h-4 w-4" />
              </div>
              <span className="text-[10px] font-medium text-slate-600">{a.label}</span>
            </Link>
          ))}
        </div>

        {/* Recent transactions */}
        {txns.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-white/90 bg-white/80 shadow-[0_16px_44px_-20px_rgba(15,23,42,0.28)] backdrop-blur-md">
            <div className="flex items-center justify-between border-b border-indigo-50/80 px-4 py-2.5">
              <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Recent transactions</p>
              <Link to="/earnings" className="text-[10px] font-medium text-indigo-600 no-underline transition hover:text-indigo-800">
                View all →
              </Link>
            </div>
            <div className="divide-y divide-slate-100/80 px-1 sm:px-2">
              {txns.map(txn => {
                const t = txnTypeLabel[txn.txn_type] || { label: txn.txn_type, color: 'text-slate-600', bg: 'bg-slate-50', sign: '' };
                return (
                  <div key={txn.id} className="flex items-center gap-2.5 px-2.5 py-2.5 sm:px-3">
                    <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-xs font-semibold ${t.bg} ${t.color}`}>
                      {t.sign}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium text-slate-700">{txn.description}</p>
                      <p className="text-[10px] text-slate-500">{formatAppDate(txn.created_at)}</p>
                    </div>
                    <span className={`flex-shrink-0 text-xs font-semibold ${txn.points > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {txn.points > 0 ? '+' : ''}{txn.points} pts
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;